import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Stack,
  Chip,
  Box,
  Paper,
  Avatar,
  IconButton,
  CircularProgress,
  Fade,
  Slide,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material';

import {
  Assignment,
  Person,
  CloseRounded,
  Notes,
  History,
  ChatBubbleOutline,
  AttachFile,
  Send,
  InsertDriveFile as InsertDriveFileIcon,
  Image as ImageIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  TableChart as TableChartIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { errorTost } from '../../Services/Toast';
import { HELPDESK_BASE_URL, HELPDESK_TENANT_ID, buildHelpDeskUser } from './helpDeskConfig';

const PRIMARY = '#1570EF';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TicketDetailsModal = ({ open, onClose, ticket }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:768px)');
  const loggedInUser = useSelector((state) => state.Auth.user);
  const hdUser = buildHelpDeskUser(loggedInUser);

  // --------------- STATES --------------
  const [activeTab, setActiveTab] = useState(0);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  const [newComment, setNewComment] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [sendingComment, setSendingComment] = useState(false);

  const [history, setHistory] = useState([]);
  const [assignee, setAssignee] = useState([]);

  const latestAssignedUser = React.useMemo(() => {
    if (!Array.isArray(history) || history.length === 0) return null;
    const sorted = [...history].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    const latestWithUser = sorted.find((h) => h.users);
    return latestWithUser?.users || null;
  }, [history]);

  const TABS = [
    { label: 'Issue Overview', icon: <Notes /> },
    { label: 'Ticket Ownership', icon: <Assignment /> },
    { label: 'Ticket History', icon: <History /> },
    { label: `Comments (${comments.length})`, icon: <ChatBubbleOutline /> },
  ];

  const [mobileTabAnchor, setMobileTabAnchor] = useState(null);
  const openMobileMenu = Boolean(mobileTabAnchor);
  const handleMobileTabOpen = (event) => setMobileTabAnchor(event.currentTarget);
  const handleMobileTabSelect = (index) => {
    setActiveTab(index);
    setMobileTabAnchor(null);
  };

  // --------------- API --------------
  const fetchHistory = async () => {
    try {
      const res = await axios.get(
        `${HELPDESK_BASE_URL}/api/user/getReassingHistory/${ticket.id}`
      );
      let historyData = [];
      if (Array.isArray(res.data)) historyData = res.data;
      else if (res.data?.history) historyData = res.data.history;

      historyData.sort(
        (a, b) =>
          new Date(b.ticket_assigned_at || b.created_at) -
          new Date(a.ticket_assigned_at || a.created_at)
      );
      setHistory(historyData);
    } catch (err) {
      console.error('Error fetching ticket history:', err);
      setHistory([]);
    }
  };

  const fetchTicketDetails = async () => {
    try {
      const res = await axios.get(
        `${HELPDESK_BASE_URL}/api/user/getTicket/${ticket.id}`
      );
      if (res?.data?.error) {
        console.error('Failed to fetch ticket details:', res.data.error);
        errorTost('Failed to fetch ticket details');
        return;
      }
      setAssignee(res?.data?.user || []);
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      setAssignee([]);
    }
  };

  const fetchComments = async () => {
    try {
      setLoadingComments(true);
      const res = await axios.get(
        `${HELPDESK_BASE_URL}/api/user/comments/${ticket.id}`
      );
      if (res.data.success) setComments(res.data.comments);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const refreshComments = () => fetchComments();
  const removeAttachment = () => setSelectedFile(null);

  const handleSendComment = async () => {
    if (!newComment.trim() && !selectedFile) return;
    setSendingComment(true);
    try {
      const formData = new FormData();
      formData.append('ticket_id', ticket.id);
      formData.append('message', newComment);
      formData.append('userEmail', assignee?.email || null);
      formData.append('display_id', ticket.display_id);
      formData.append('mobile', assignee?.mobile || null);
      formData.append('name', hdUser.name);
      formData.append('tenant_id', HELPDESK_TENANT_ID);

      if (selectedFile) formData.append('attachments', selectedFile);
      else formData.append('attachments', '');

      const res = await axios.post(
        `${HELPDESK_BASE_URL}/api/user/add-comment`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (res.data.success) {
        setNewComment('');
        setSelectedFile(null);
        refreshComments();
      } else {
        console.error('Error adding comment:', res.data.message);
        errorTost('Failed to add comment.');
      }
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setSendingComment(false);
    }
  };

  // --------------- HELPERS --------------
  const getFileName = (url = '') => url.substring(url.lastIndexOf('-') + 1);

  const getFileIcon = (fileName = '') => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext))
      return <ImageIcon sx={{ color: PRIMARY }} />;
    if (['xls', 'xlsx', 'csv'].includes(ext))
      return <TableChartIcon sx={{ color: '#2e7d32' }} />;
    if (ext === 'pdf') return <PictureAsPdfIcon sx={{ color: '#d32f2f' }} />;
    if (['doc', 'docx'].includes(ext))
      return <DescriptionIcon sx={{ color: '#1976d2' }} />;
    return <InsertDriveFileIcon />;
  };

  const isImage = (fileName = '') =>
    ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(
      fileName.split('.').pop().toLowerCase()
    );

  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      : '-';

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'resolved':
        return 'success';
      case 'open':
        return 'warning';
      case 'closed':
        return 'default';
      default:
        return 'primary';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      case 'low':
        return theme.palette.success.main;
      default:
        return theme.palette.info.main;
    }
  };

  const capitalizeFirst = (text = '') => {
    const str = text.trim();
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
  };
  const capitalizeAll = (text = '') => text.toUpperCase();

  // --------------- TABS / EFFECTS --------------
  const handleTabChange = (e, v) => setActiveTab(v);

  useEffect(() => {
    if (open) fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if ((activeTab === 2 || activeTab === 1) && open) fetchHistory();
    else if (activeTab === 3 && open) fetchTicketDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, open]);

  if (!ticket) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.15)}`,
          minHeight: '95vh',
        },
      }}>
      {/* HEADER */}
      <DialogTitle sx={{ background: PRIMARY, color: 'white', p: 3, pb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: alpha('#fff', 0.2), color: 'white', width: 48, height: 48 }}>
              <Assignment />
            </Avatar>
            <Box>
              <Typography
                noWrap
                sx={{
                  fontWeight: 700,
                  fontFamily: 'Roboto-Regular',
                  fontSize: { xs: 14, md: 16 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                Ticket #{ticket.display_id ?? 'N/A'}
              </Typography>
              <Chip
                label={ticket.status?.toUpperCase() ?? 'N/A'}
                color={getStatusColor(ticket.status)}
                size="small"
                sx={{ bgcolor: alpha('#fff', 0.2), color: 'white', fontWeight: 600 }}
              />
            </Box>
          </Stack>
          <IconButton sx={{ color: 'white' }} onClick={onClose}>
            <CloseRounded />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* TABS */}
      <Box sx={{ px: 3, backgroundColor: '#f7f9fa' }}>
        {!isMobile && (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            TabIndicatorProps={{ style: { backgroundColor: PRIMARY, height: 3 } }}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontFamily: 'Roboto-Regular',
                fontWeight: 600,
                color: '#555',
                opacity: 1,
                minHeight: '60px',
                fontSize: '1.6rem',
                px: 3,
              },
              '& .MuiTab-root svg': { color: '#888', fontSize: '2.2rem' },
              '& .Mui-selected': { color: `${PRIMARY} !important` },
              '& .Mui-selected svg': { color: `${PRIMARY} !important` },
            }}>
            {TABS.map((tab, index) => (
              <Tab key={index} icon={tab.icon} iconPosition="start" label={tab.label} />
            ))}
          </Tabs>
        )}

        {isMobile && (
          <Box sx={{ py: 1 }}>
            <Box
              onClick={handleMobileTabOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                bgcolor: '#fff',
                cursor: 'pointer',
                marginTop: '10px',
              }}>
              <Stack direction="row" spacing={1.2} alignItems="center">
                {TABS[activeTab].icon}
                <Typography sx={{ fontFamily: 'Roboto-Regular', fontWeight: 600, color: '#111827' }}>
                  {TABS[activeTab].label}
                </Typography>
              </Stack>
              <ExpandMoreIcon sx={{ color: '#6B7280' }} />
            </Box>

            <Menu
              anchorEl={mobileTabAnchor}
              open={openMobileMenu}
              onClose={() => setMobileTabAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 260 } }}>
              {TABS.map((tab, index) => (
                <MenuItem
                  key={index}
                  selected={index === activeTab}
                  onClick={() => handleMobileTabSelect(index)}
                  sx={{ fontFamily: 'Roboto-Regular', fontWeight: index === activeTab ? 600 : 500 }}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    {tab.icon}
                    <Typography>{tab.label}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </Box>

      {/* CONTENT */}
      <DialogContent sx={{ p: 3, backgroundColor: '#f7f9fa' }}>
        {/* ISSUE OVERVIEW */}
        {activeTab === 0 && (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, fontFamily: 'Roboto-Regular' }}>
              Issue Overview
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
              {/* LEFT */}
              <Box flex={1}>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                  Subject:
                </Typography>
                <Typography sx={{ mb: 1, fontFamily: 'Roboto-Regular' }}>{ticket.subject_line ?? 'N/A'}</Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                  Description:
                </Typography>
                <Typography sx={{ mb: 1.5, fontFamily: 'Roboto-Regular' }}>{ticket.description ?? 'N/A'}</Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                  Current Status:
                </Typography>
                <Chip sx={{ mt: 1, fontWeight: 'bold' }} label={capitalizeAll(ticket?.status) || 'N/A'} color={getStatusColor(ticket?.status)} />
                <Divider sx={{ mt: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                  Priority:
                </Typography>
                <Chip
                  label={capitalizeAll(ticket?.priority_level) || 'N/A'}
                  sx={{
                    mt: 1,
                    bgcolor: alpha(getPriorityColor(ticket?.priority_level), 0.1),
                    color: getPriorityColor(ticket?.priority_level),
                    fontWeight: 600,
                    fontFamily: 'Roboto-Regular',
                  }}
                />
                <Divider sx={{ mt: 2 }} />
              </Box>

              {/* RIGHT */}
              <Box flex={1}>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                  Department:
                </Typography>
                <Typography sx={{ mb: 1, fontFamily: 'Roboto-Regular' }}>{ticket?.department_name || 'N/A'}</Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                  Category:
                </Typography>
                <Typography sx={{ mb: 1, fontFamily: 'Roboto-Regular' }}>{ticket?.category_name || 'N/A'}</Typography>
                <Divider sx={{ mb: 2 }} />

                {ticket?.subcategory_name && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                      Sub Category:
                    </Typography>
                    <Typography sx={{ mb: 1, fontFamily: 'Roboto-Regular' }}>{ticket?.subcategory_name || 'N/A'}</Typography>
                    <Divider sx={{ mb: 2 }} />
                  </>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                  Ticket Timeline:
                </Typography>
                <Box sx={{ mt: 1, fontFamily: 'Roboto-Regular' }}>
                  <Typography variant="caption" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                    Created:
                  </Typography>
                  <Typography>{formatDate(ticket?.created_at) || 'N/A'}</Typography>
                  <Typography variant="caption" sx={{ mt: 2, fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                    Last Updated:
                  </Typography>
                  <Typography sx={{ fontFamily: 'Roboto-Regular' }}>{formatDate(ticket?.last_updated_at) || 'N/A'}</Typography>
                </Box>
              </Box>
            </Stack>

            {/* ATTACHMENTS */}
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', mb: 1, fontWeight: 'bold' }}>
                Attachments:
              </Typography>
              {(() => {
                let attachments = [];
                try {
                  attachments = JSON.parse(ticket?.attachments || '[]');
                } catch (e) {
                  attachments = [];
                }
                return attachments.length > 0 ? (
                  <Box
                    sx={{
                      display: { xs: 'flex', md: 'grid' },
                      flexWrap: { xs: 'wrap', md: 'unset' },
                      gridTemplateColumns: { md: 'repeat(auto-fill, minmax(280px, 1fr))' },
                      gap: 2,
                      width: '100%',
                    }}>
                    {attachments.map((img, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: { xs: '100%', sm: 300, md: '100%' },
                          height: { xs: 200, md: 260 },
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '1px solid #E5E7EB',
                          cursor: 'pointer',
                          bgcolor: '#fff',
                        }}
                        onClick={() => window.open(img, '_blank')}>
                        <img src={img} alt={`Attachment ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular' }}>
                    No attachments available
                  </Typography>
                );
              })()}
            </Box>
          </Paper>
        )}

        {/* TICKET OWNERSHIP */}
        {activeTab === 1 && (
          <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontFamily: 'Roboto-Regular' }}>
              Ticket Ownership
            </Typography>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                Submitted by:
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                  Name:
                </Typography>
                <Typography sx={{ fontWeight: 600, fontFamily: 'Roboto-Regular' }}>{hdUser.name || 'N/A'}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                  Email:
                </Typography>
                <Typography sx={{ fontWeight: 600, fontFamily: 'Roboto-Regular' }}>{hdUser.email || 'N/A'}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                  Mobile:
                </Typography>
                <Typography sx={{ fontWeight: 600, fontFamily: 'Roboto-Regular' }}>{hdUser.mobile || 'N/A'}</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: { xs: 2, md: 3 }, borderColor: alpha(theme.palette.divider, 0.3) }} />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 'bold' }}>
                Assigned To:
              </Typography>
              {latestAssignedUser ? (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                      Name :
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontFamily: 'Roboto-Regular' }}>{latestAssignedUser?.name ?? 'N/A'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 'bold' }}>
                      Email :
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontFamily: 'Roboto-Regular' }}>{latestAssignedUser?.email ?? 'N/A'}</Typography>
                  </Box>
                </>
              ) : (
                <Typography color="text.secondary">Not assigned yet</Typography>
              )}
            </Box>
          </Paper>
        )}

        {/* TICKET HISTORY */}
        {activeTab === 2 && (
          <Fade in timeout={500}>
            <Paper
              sx={{
                px: isMobile ? 2 : 4,
                paddingBottom: isMobile ? 2 : 4,
                borderRadius: 3,
                fontFamily: 'Roboto-Regular, sans-serif',
                bgcolor: '#F9FAFB',
                maxHeight: '60vh',
                overflowY: 'auto',
                position: 'relative',
              }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#F9FAFB', pt: 1, pb: 1, mb: 2 }}>
                <Typography
                  fontWeight={700}
                  sx={{ fontSize: 20, py: 2, px: isMobile ? 2 : 4, color: '#1E293B', fontFamily: 'Roboto-Regular' }}>
                  Ticket History
                </Typography>
              </Stack>

              {history.length === 0 ? (
                <Typography color="text.secondary" sx={{ fontSize: '1.5rem' }}>
                  No history available.
                </Typography>
              ) : (
                <Box sx={{ position: 'relative', pl: isMobile ? 0 : 2 }}>
                  <Stack spacing={2} sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 20, width: '2px', bgcolor: '#CBD5E1' }} />
                    {history.map((h, i) => (
                      <Stack key={i} direction="row" spacing={2} alignItems="flex-start">
                        <Box sx={{ position: 'relative', minWidth: '40px', height: '100%', display: 'flex', justifyContent: 'center' }}>
                          <Box
                            sx={{
                              width: isMobile ? 10 : 14,
                              height: isMobile ? 10 : 14,
                              borderRadius: '50%',
                              backgroundColor: PRIMARY,
                              border: '3px solid white',
                              boxShadow: '0 0 0 2px #CBD5E1',
                              mt: 0.5,
                            }}
                          />
                        </Box>
                        <Paper
                          elevation={2}
                          sx={{
                            p: isMobile ? 2 : 3,
                            borderRadius: 2,
                            bgcolor: 'white',
                            borderLeft: `4px solid ${PRIMARY}`,
                            boxShadow: '0px 4px 12px rgba(0,0,0,0.04)',
                            whiteSpace: 'pre-line',
                            mt: 1,
                            fontFamily: 'Roboto-Regular',
                            color: 'black',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            maxWidth: '100%',
                          }}>
                          <Typography sx={{ fontWeight: 700, fontSize: isMobile ? 14 : 18, color: PRIMARY, mb: 0.5 }}>
                            {capitalizeFirst(h.actions || h.message ? 'Comment' : '')}
                          </Typography>
                          {h.description && (
                            <Typography sx={{ fontSize: isMobile ? 14 : 18, mb: 0.5 }}>
                              <strong>Description:</strong> {h?.description || 'N/A'}
                            </Typography>
                          )}
                          {h.message && (
                            <Typography sx={{ fontSize: isMobile ? 14 : 15, fontStyle: 'italic', color: '#334155', mb: 1 }}>
                              {h?.message || 'N/A'}
                            </Typography>
                          )}
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={{ xs: 0.5, md: 2 }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                            sx={{ mt: 1 }}>
                            <Typography sx={{ fontSize: 14, color: '#64748B', fontWeight: 500 }}>
                              On: {formatDate(h.ticket_assigned_at || h.created_at) || 'N/A'}
                            </Typography>
                            <Typography sx={{ fontSize: 14, color: '#64748B', fontWeight: 500 }}>
                              Action by: {h?.user_name || 'You'}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}
            </Paper>
          </Fade>
        )}

        {/* COMMENTS TAB */}
        {activeTab === 3 && (
          <Box>
            <Paper
              elevation={0}
              sx={{
                px: isMobile ? 2 : 4,
                paddingBottom: isMobile ? 2 : 4,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                fontFamily: 'Roboto-Regular',
                overflowY: 'auto',
                minHeight: isMobile ? '50vh' : '43vh',
                maxHeight: '35vh',
              }}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mb: 2, paddingTop: isMobile ? 2 : 3, position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#F9FAFB', pb: 1 }}>
                <Avatar sx={{ bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY }}>
                  <ChatBubbleOutline />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Roboto-Regular' }}>
                  Comments ({comments.length})
                </Typography>
              </Stack>

              {loadingComments ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : comments.length === 0 ? (
                <Typography color="text.secondary" sx={{ fontFamily: 'Roboto-Regular' }}>
                  No comments yet.
                </Typography>
              ) : (
                comments.map((comment) => {
                  const isUser = comment.user_id === null;
                  const img =
                    (comment.attachments && comment.attachments !== '[]') ||
                    (comment.link && comment.link !== '[]') ||
                    (comment.image && comment.image !== '[]') ||
                    null;

                  // OUTGOING (YOU)
                  if (isUser) {
                    return (
                      <Fade in key={comment.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, width: '100%' }}>
                          <Box
                            sx={{
                              p: 2,
                              maxWidth: '100%',
                              minWidth: { xs: '60%', md: '50%' },
                              borderRadius: 2,
                              bgcolor: '#EFF8FF',
                              border: `1px solid ${alpha(PRIMARY, 0.4)}`,
                              fontFamily: 'Roboto-Regular',
                            }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar sx={{ bgcolor: PRIMARY, width: 28, height: 28 }}>
                                {hdUser?.name?.charAt(0) || 'U'}
                              </Avatar>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black', fontFamily: 'Roboto-Regular' }}>
                                You
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: 'pre-line', mt: 1, fontFamily: 'Roboto-Regular', color: 'black', wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: '100%' }}>
                              {comment?.message || 'N/A'}
                            </Typography>

                            {comment?.image &&
                              comment.image !== '[]' &&
                              (() => {
                                let parsedImage;
                                try {
                                  parsedImage = JSON.parse(comment.image);
                                } catch (e) {
                                  return null;
                                }
                                const fileUrl = parsedImage?.[0]?.url;
                                const fileName = parsedImage?.[0]?.filename || '';
                                if (!fileUrl) return null;
                                const image = isImage(fileName);
                                return (
                                  <Box
                                    sx={{ mt: 1, p: 1, borderRadius: '8px', border: '1px solid #E5E7EB', bgcolor: '#fff', cursor: 'pointer' }}
                                    onClick={() => window.open(fileUrl, '_blank')}>
                                    {image && (
                                      <Box sx={{ mb: 1 }}>
                                        <img src={fileUrl} alt={fileName} style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', borderRadius: '6px' }} />
                                      </Box>
                                    )}
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      {getFileIcon(fileName)}
                                      <Typography variant="body2" sx={{ fontFamily: 'Roboto-Regular', fontWeight: 500, color: '#111827', wordBreak: 'break-all', flex: 1 }}>
                                        {fileName}
                                      </Typography>
                                    </Stack>
                                  </Box>
                                );
                              })()}

                            <Typography variant="caption" color="black" sx={{ mt: 1, display: 'block', textAlign: 'left' }}>
                              {formatDate(comment?.created_at) || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Fade>
                    );
                  }

                  // INCOMING
                  return (
                    <Fade in key={comment.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, width: '100%' }}>
                        <Box
                          sx={{
                            p: 2,
                            maxWidth: '100%',
                            minWidth: { xs: '60%', md: '50%' },
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.background.default, 0.6),
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            fontFamily: 'Roboto-Regular',
                            overflow: 'hidden',
                          }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 28, height: 28 }}>
                              {comment.user_name?.charAt(0) || 'A'}
                            </Avatar>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {comment?.user_name || 'N/A'}
                            </Typography>
                          </Stack>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-line', mt: 1, fontFamily: 'Roboto-Regular', color: 'black', wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: '100%' }}>
                            {comment?.message || 'N/A'}
                          </Typography>
                          {img && (
                            <Box sx={{ mt: 1 }} onClick={() => window.open(img, '_blank')} style={{ cursor: 'pointer' }}>
                              <img src={img} alt="attachment" style={{ width: '100%', maxHeight: '180px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #E5E7EB' }} />
                            </Box>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {formatDate(comment?.created_at) || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </Fade>
                  );
                })
              )}
            </Paper>

            {/* ADD COMMENT */}
            <Box>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={2}>
                {!isMobile && (
                  <Avatar>
                    <Person />
                  </Avatar>
                )}
                <Box flex={1}>
                  <textarea
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    style={{
                      width: '94%',
                      minHeight: '60px',
                      padding: '8px',
                      borderRadius: '8px',
                      border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                      fontSize: '14px',
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'Roboto-Regular',
                    }}
                  />
                </Box>
              </Stack>

              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Roboto-Regular' }}>
                  {selectedFile.name}{' '}
                  <span
                    onClick={removeAttachment}
                    style={{
                      backgroundColor: '#c9c6c6',
                      color: 'red',
                      fontSize: 12,
                      padding: '2px 5px',
                      marginLeft: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      borderRadius: 5,
                    }}>
                    Remove
                  </span>
                </Typography>
              )}

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                <Button
                  component="label"
                  startIcon={<AttachFile />}
                  sx={{ textTransform: 'none', color: PRIMARY, fontFamily: 'Roboto-Regular' }}>
                  Attach
                  <input
                    hidden
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  />
                </Button>
                <Button
                  onClick={handleSendComment}
                  disabled={sendingComment}
                  endIcon={!sendingComment && <Send />}
                  sx={{
                    bgcolor: sendingComment ? '#ccc' : PRIMARY,
                    color: '#fff',
                    textTransform: 'none',
                    fontFamily: 'Roboto-Regular',
                    px: 3,
                    '&:hover': { bgcolor: '#0E5BC4' },
                  }}>
                  {sendingComment ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send'}
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TicketDetailsModal;
